import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import TranslationNS from '@/utils/Constants/TranslationNS'

import './index.css'

/** Fired on `window` by the welcome card's "Learn more": the chip opens its popover. */
export const WHATS_NEW_EVENT = 'scoring:whatsnew'

export function SparkleIcon({ className, strokeWidth = 1.6 }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M11 3.5l1.9 5.1 5.1 1.9-5.1 1.9L11 17.5l-1.9-5.1L4 10.5l5.1-1.9z" />
            <path d="M18.5 15l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9z" />
        </svg>
    )
}

/**
 * Design transition (mock `_kit/transition.html`, Oct 2 2026, William — no Figma): "New" chip right
 * after the nav logo, with a popover explaining the page-by-page rollout. Monochrome, outlined
 * "Got it", no feedback link (destination undecided). `compact` = mobile nav: sparkle only, 28px
 * square, popover fixed full-width under the nav and closed on scroll. Escape is handled in the
 * capture phase so an open album / dialog underneath stays open.
 */
export default function NewDesignChip({ compact = false }) {
    const { t } = useTranslation(TranslationNS.DESIGN_TRANSITION)

    const [open, setOpen] = useState(false)
    const [top, setTop] = useState()
    const wrapRef = useRef(null)
    const chipRef = useRef(null)
    const popRef = useRef(null)

    const close = (focusChip) => {
        setOpen(false)
        if (focusChip) chipRef.current?.focus()
    }

    useEffect(() => {
        const onWhatsNew = () => setOpen(true)
        window.addEventListener(WHATS_NEW_EVENT, onWhatsNew)
        return () => window.removeEventListener(WHATS_NEW_EVENT, onWhatsNew)
    }, [])

    useEffect(() => {
        if (!open) return undefined
        // compact: the popover is fixed, its top follows the bottom of the nav (fixed on "/" only)
        const nav = chipRef.current?.closest('nav')
        if (nav) setTop(Math.round(nav.getBoundingClientRect().bottom + 8))
        popRef.current?.querySelector('.rd-btn')?.focus()

        const onKey = (event) => {
            if (event.key !== 'Escape') return
            event.stopPropagation()
            close(true)
        }
        const onPointerDown = (event) => {
            if (!wrapRef.current?.contains(event.target)) close(false)
        }
        const onScroll = () => {
            if (compact) close(false)
        }
        window.addEventListener('keydown', onKey, true)
        document.addEventListener('pointerdown', onPointerDown)
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => {
            window.removeEventListener('keydown', onKey, true)
            document.removeEventListener('pointerdown', onPointerDown)
            window.removeEventListener('scroll', onScroll)
        }
    }, [open, compact])

    return (
        <span ref={wrapRef} className="rd-wrap">
            <button
                ref={chipRef}
                type="button"
                className={`rd-chip ${compact ? 'rd-chip--compact' : ''}`}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-controls="rdPop"
                aria-label={t('New scoring.fit design: learn more')}
                onClick={() => (open ? close(false) : setOpen(true))}
            >
                <SparkleIcon className="rd-chip-ico" strokeWidth={2} />
                {!compact && <span className="rd-chip-lbl">{t('New')}</span>}
            </button>

            {open && (
                <div
                    ref={popRef}
                    id="rdPop"
                    role="dialog"
                    aria-labelledby="rdPopTtl"
                    className={`rd-pop ${compact ? 'rd-pop--fixed' : ''}`}
                    style={compact ? { top } : undefined}
                >
                    <div className="rd-head">
                        <SparkleIcon className="rd-ico" />
                        <p className="rd-ttl" id="rdPopTtl">
                            {t('The new scoring.fit is rolling out')}
                        </p>
                    </div>
                    <p className="rd-txt">
                        {t(
                            "You're on a page with the new design. We're rolling it out page by page: some pages keep the previous interface for a while. That's not a bug, they will follow.",
                        )}
                    </p>
                    <div className="rd-acts">
                        <button type="button" className="rd-btn" onClick={() => close(true)}>
                            {t('Got it')}
                        </button>
                    </div>
                </div>
            )}
        </span>
    )
}
